import { ListPhoneNumbersForLocationsResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-numbers/phone_numbers_service.pb';
import { useMutation, useQueryClient } from 'react-query';
import { DialQueries } from '@frontend/api-dialpad';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { PortingQueries } from '@frontend/api-porting';
import { usePhoneConfigStore } from '@frontend/phone-config';
import { PhoneNumberSchemaService, SchemaTenDLCCoordinator } from '@frontend/schema';
import {
  RegisterPhoneNumberPayload,
  SetLocationSmsPayload,
  UpdateNamePayload,
  UpdateOutboundCallerIdPayload,
} from './types';

export const usePhoneNumbersTableMutations = (selectedRowId: string[]) => {
  const queryClient = useQueryClient();
  const phoneConfigSipProfileId = usePhoneConfigStore((store) => store.phoneConfig?.sipProfileId);

  const register = useMutation({
    mutationFn: (payload: RegisterPhoneNumberPayload) => {
      return SchemaTenDLCCoordinator.RegisterPhoneNumber(payload, {
        headers: {
          'Location-Id': payload.locationId,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(PortingQueries.queryKeys.phoneNumbers(selectedRowId));
    },
  });

  const setLocationSms = useMutation({
    mutationFn: (payload: SetLocationSmsPayload) => {
      return PhoneNumberSchemaService.SetLocationSms(payload);
    },
    onSettled: () => {
      queryClient.invalidateQueries(PortingQueries.queryKeys.phoneNumbers(selectedRowId));
    },
  });

  const updateName = useMutation({
    mutationFn: (payload: UpdateNamePayload) => {
      return PhoneNumberSchemaService.UpdateDisplayName(payload);
    },
    onSettled: () => {
      queryClient.invalidateQueries([PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations']);
      queryClient.invalidateQueries(DialQueries.queryKeys.getNumbersBySipProfileId(phoneConfigSipProfileId ?? ''));
    },
  });

  const updateCallerOutboundId = useMutation({
    mutationFn: (payload: UpdateOutboundCallerIdPayload) => {
      return PhoneNumberSchemaService.UpdateOutboundCallerID(payload);
    },
    onMutate: async (res) => {
      await queryClient.cancelQueries([PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations']);
      const previousCache = queryClient.getQueryData<ListPhoneNumbersForLocationsResponse['phoneNumbers']>([
        PhoneNumbersV1.Service.serviceName,
        'ListPhoneNumbersForLocations',
      ]);

      queryClient.setQueryData<ListPhoneNumbersForLocationsResponse['phoneNumbers']>(
        [PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations'],
        (old) => {
          if (!old) return old;
          return old.map((item) => {
            return item.id === res.phoneNumberId
              ? {
                  ...item,
                  capabilities: {
                    ...item.capabilities,
                    voiceCapabilities: {
                      ...item.capabilities?.voiceCapabilities,
                      outboundCallerIdEnabled: res.enabled ?? false,
                    },
                  },
                }
              : item;
          });
        }
      );

      return { previousCache };
    },
    onError: (_err, _newNumber, context) => {
      queryClient.setQueryData(
        [PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations'],
        context?.previousCache
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries([PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations']);
      queryClient.invalidateQueries(DialQueries.queryKeys.getNumbersBySipProfileId(phoneConfigSipProfileId ?? ''));
    },
  });

  return {
    register,
    setLocationSms,
    updateName,
    updateCallerOutboundId,
  };
};
